/* ──────────────────────────────────────────────────────────────────────────
 * Apollo Industries — Compact bearing cross-reference widget (client JS)
 * assets/bearing-xref-mini.js
 *
 * Reads `.ifx-mini` containers, fetches /api/search for each one's PN,
 * and renders up to N equivalents. Filters out the source PN itself, and
 * prefers showing one entry per brand to keep the list diverse.
 *
 * Uses window.IFX (interchange-utils.js, loaded first by the snippet) for
 * escaping + analytics; degrades gracefully if utils failed to load.
 * The loading state is the server-rendered `.ifx-mini__loading` row — CSS
 * renders it as a skeleton shimmer (static gray under reduced motion).
 * ───────────────────────────────────────────────────────────────────────── */
(function () {
  "use strict";

  var U = window.IFX;
  if (!U) {
    try { console.error("[ifx-mini] interchange-utils.js missing — using degraded local fallbacks"); } catch (e) { /* noop */ }
    U = {
      escapeHtml: function (s) {
        return String(s == null ? "" : s).replace(/[&<>"']/g, function (c) {
          return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
        });
      },
      track: function () { /* degraded: no analytics */ },
    };
  }

  const containers = document.querySelectorAll(".ifx-mini");
  containers.forEach(initMini);

  async function initMini(el) {
    const apiBase = el.dataset.apiBase || "";
    const pn = (el.dataset.pn || "").trim();
    const limit = Math.max(1, Math.min(20, parseInt(el.dataset.limit || "8", 10)));
    const list = el.querySelector(".ifx-mini__list");
    if (!pn || !list) return;

    U.track("ifx_mini_view", { pn: pn });

    // Skeleton shimmer while loading (CSS handles the visual; the text stays
    // in the DOM for the aria-live announcement).
    list.innerHTML = '<li class="ifx-mini__loading">Loading equivalents&hellip;</li>';

    try {
      const url = new URL(apiBase + "/api/search");
      url.searchParams.set("q", pn);
      url.searchParams.set("limit", String(limit * 3)); // overfetch for de-dup
      const r = await fetch(url.toString(), { credentials: "omit" });
      if (!r.ok) throw new Error("API " + r.status);
      const data = await r.json();

      const all = (data.primary ? [data.primary] : []).concat(data.equivalents || []);
      const sourceNorm = pn.toUpperCase().replace(/[\s\-/.]/g, "");
      // Filter: drop the exact PN we searched for and reference-grade
      // listings, prefer rows whose dimensions agree with the family, then
      // dedup by brand so the list stays diverse.
      const seenBrands = new Set();
      const others = [];
      const ranked = all.filter((b) => !b.dimsSuspect).concat(all.filter((b) => b.dimsSuspect));
      for (const b of ranked) {
        const bnNorm = (b.partNumber || "").toUpperCase().replace(/[\s\-/.]/g, "");
        if (bnNorm === sourceNorm) continue;
        if (b.brandQuality === "junk" || b.brandQuality === "oem") continue;
        const brandKey = (b.brand || "").toLowerCase();
        if (seenBrands.has(brandKey)) continue;
        seenBrands.add(brandKey);
        others.push(b);
        if (others.length >= limit) break;
      }

      if (others.length === 0) {
        list.innerHTML = '<li class="ifx-mini__empty">No cross-references in our database yet for this part.</li>';
        return;
      }

      const brandName = typeof U.brandName === "function" ? U.brandName : function (s) { return String(s || "").toUpperCase(); };
      const toolUrl = el.dataset.toolUrl || "";
      list.innerHTML = others.map((b) =>
        '<li class="ifx-mini__item">'
        + '<span class="ifx-mini__brand">' + U.escapeHtml(brandName(b.brand)) + "</span>"
        + (toolUrl
          ? '<a class="ifx-mini__pn" href="' + U.escapeHtml(toolUrl) + "?q=" + encodeURIComponent(b.partNumber || "") + '">' + U.escapeHtml(b.partNumber || "") + "</a>"
          : '<span class="ifx-mini__pn">' + U.escapeHtml(b.partNumber || "") + "</span>")
        + (b.xrefType === "verified" || b.xrefType === "iso_equivalent"
          ? '<span class="ifx-mini__tag">' + (b.xrefType === "verified" ? "verified" : "ISO-equivalent") + "</span>"
          : "")
        + "</li>"
      ).join("");
    } catch (err) {
      console.error("[ifx-mini] load failed", err);
      list.innerHTML = '<li class="ifx-mini__empty">Could not load cross-references right now.</li>';
    }
  }
})();
